"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { FileText } from "lucide-react";

// Color palette for template icons based on template type or category
const colorPalettes: Record<string, {
  bg: string;
  border: string;
  iconColor: string;
}> = {
  discovery: { 
    bg: 'bg-template-blue/80',
    border: 'border-template-blue',
    iconColor: 'text-info-text',
  },
  definition: { 
    bg: 'bg-template-purple/80',
    border: 'border-template-purple',
    iconColor: 'text-brand-700',
  },
  delivery: { 
    bg: 'bg-template-green/80',
    border: 'border-template-green',
    iconColor: 'text-success-text',
  },
  project: { 
    bg: 'bg-template-orange/80',
    border: 'border-template-orange',
    iconColor: 'text-warning-text',
  },
  custom: { 
    bg: 'bg-template-pink/80',
    border: 'border-template-pink',
    iconColor: 'text-error-text',
  },
};

const defaultPalette = {
  bg: 'bg-surface-secondary',
  border: 'border-surface-border',
  iconColor: 'text-gray-600',
};

interface TemplateIconProps {
  icon?: string | null;
  category?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  xs: 'w-6 h-6 text-sm rounded-md',
  sm: 'w-8 h-8 text-lg rounded-lg',
  md: 'w-10 h-10 text-xl rounded-xl',
  lg: 'w-14 h-14 text-2xl rounded-xl',
};

export function TemplateIcon({ 
  icon, 
  category = 'custom',
  size = 'sm',
  className 
}: TemplateIconProps) {
  const palette = colorPalettes[category.toLowerCase()] || defaultPalette;

  return (
    <div 
      className={cn(
        'flex items-center justify-center border shrink-0',
        sizeClasses[size],
        palette.bg,
        palette.border,
        className
      )}
    >
      {icon ? (
        <span>{icon}</span>
      ) : (
        <FileText className={cn('w-4 h-4', palette.iconColor)} />
      )}
    </div>
  );
}
